import { useState } from "react";
import { find } from "lodash";
import { useMutation } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { fetchApi } from "../../../helpers/reactQueryApi";
import RecurringAppointmentDecisionModal from "../../shared/RecurringAppointmentDecisionModal";
import { Appointment } from "../../../@types/appointments";

// Accepting an appointment straight from the widget card (tixxt/core#469), so
// a member does not have to take the detour through the appointments module.
// Only accepting is offered here; changing or withdrawing an answer stays in
// the module, which has the full RSVP controls.
export default function AppointmentParticipateButton({
  appointment,
}: {
  appointment: Appointment;
}) {
  const myParticipation = find(appointment.participants, { id: "me" });
  const [accepted, setAccepted] = useState(
    myParticipation?.status === "accepted",
  );
  const [showDecisionModal, setShowDecisionModal] = useState(false);

  const { mutate, isLoading } = useMutation<
    unknown,
    Error,
    { apply_to?: string }
  >(
    ({ apply_to }) =>
      fetchApi(`/appointments/${appointment.id}/participants/me`, {
        method: "PUT",
        body: { status: "accepted", apply_to },
      }),
    { onSuccess: () => setAccepted(true) },
  );

  // The acceptance option itself ("Anmeldung für Teilnehmer ermöglichen"),
  // not `can.opt`, which is also true for someone merely invited.
  if (!appointment.attendee_configuration?.user_can_opt) return null;
  if (!appointment.can?.opt) return null;

  const locked =
    !!appointment.attendee_configuration?.participation_locked ||
    appointment.total_attendees_enforced_and_exceeded;

  // One occurrence of a series: the module asks whether the answer applies to
  // the series or just this date, and so does the card.
  const isOccurrence = appointment.type === "virtual_appointment";

  return (
    <>
      <RecurringAppointmentDecisionModal
        showModal={showDecisionModal}
        onClose={() => setShowDecisionModal(false)}
        handleDecision={(apply_to: string) => {
          mutate({ apply_to });
          setShowDecisionModal(false);
        }}
      />
      <button
        type="button"
        className={
          accepted
            ? "btn btn-light self-center shrink-0"
            : "btn btn-primary self-center shrink-0"
        }
        disabled={accepted || isLoading || locked}
        onClick={() => (isOccurrence ? setShowDecisionModal(true) : mutate({}))}
      >
        {isLoading && (
          <>
            <FontAwesomeIcon icon={faSpinner} spin />{" "}
          </>
        )}
        {accepted && !isLoading && (
          <>
            <FontAwesomeIcon icon={faCheck} />{" "}
          </>
        )}
        {accepted
          ? I18n.t("js.widgets.appointment.participating")
          : I18n.t("js.widgets.appointment.participate")}
      </button>
    </>
  );
}
